import type { TFunction } from 'i18next';
import type { MemberStatus, MemberType } from '../hooks/useAdminUsersQuery';
import { asUserMall } from './userMall';

/**
 * 회원 enum → 라벨 안전 조회 (G-all-3 rowEnum 노출 방지).
 *
 * i18n 키(users.status.*, users.memberType.*)가 존재하므로 t() 를 유지하되,
 * value 가 알려진 enum 이 아닐 때(null/undefined/미지원)는 템플릿 키로 조회하지 않고
 * fallback('—')을 돌려준다. 그렇지 않으면 `users.status.undefined` RAW KEY 가 노출된다.
 */

const MEMBER_STATUSES: ReadonlyArray<MemberStatus> = [
  'ACTIVE',
  'DORMANT',
  'SUSPENDED',
  'WITHDRAWN',
  // R6 O — 기업회원 승인 게이트 상태(라벨맵 users.status.* 에 동반 추가).
  'PENDING_APPROVAL',
  'REJECTED',
];
// BE 정본은 PERSONAL/BUSINESS (F-1). legacy INDIVIDUAL 도 허용해 라벨맵 키와 일치시킨다.
const MEMBER_TYPES: ReadonlyArray<MemberType> = ['PERSONAL', 'BUSINESS', 'INDIVIDUAL'];

const FALLBACK = '—';

type TFn = TFunction<'common', undefined>;

/**
 * 사업자 회원의 승인 상태는 status 로 표현된다 — PENDING_APPROVAL(승인 대기) / ACTIVE(승인) / REJECTED(반려).
 * 일반 회원의 ACTIVE 는 "정상" 이지만 사업자 회원의 ACTIVE 는 "승인" 으로 읽혀야 해서, memberType 을 주면
 * BUSINESS · ACTIVE 만 "승인" 으로 바꾼다. 그 외 상태(휴면·정지 등)는 회원 유형과 같은 라벨이다.
 */
export const memberStatusLabel = (
  t: TFn,
  value: string | null | undefined,
  memberType?: string | null,
): string => {
  if (!value || !MEMBER_STATUSES.includes(value as MemberStatus)) return FALLBACK;
  if (value === 'ACTIVE' && memberType === 'BUSINESS') return t('users.status.BUSINESS_ACTIVE', '승인');
  return t(`users.status.${value}`);
};

/**
 * QA-399 — 로그인 유형(일반/카카오/네이버/애플). signupProvider: null = 일반, KAKAO/NAVER/APPLE = SNS 가입.
 * undefined(BE 가 아직 필드를 주지 않음)는 "—" — 일반으로 단정하지 않는다.
 */
const LOGIN_TYPE_FALLBACK: Record<string, string> = { GENERAL: '일반', KAKAO: '카카오', NAVER: '네이버', APPLE: '애플' };
const providerName = (t: TFn, code: string): string => {
  const c = code.toUpperCase();
  return LOGIN_TYPE_FALLBACK[c] ? t(`users.fields.loginType${c}`, { defaultValue: LOGIN_TYPE_FALLBACK[c] }) : c;
};

/**
 * user #45 — 일반 가입 회원이 SNS 를 연동했으면 "일반 (카카오 연동)". SNS 가입 회원은 linkedProviders 에 자기 가입 SNS 가
 * 들어 있을 수 있어 signupProvider 가 null 일 때만 붙인다(BE 권장).
 */
export const loginTypeLabel = (
  t: TFn,
  signupProvider: string | null | undefined,
  linkedProviders?: ReadonlyArray<string> | null,
): string => {
  if (signupProvider === undefined) return '—';
  if (signupProvider !== null) return providerName(t, signupProvider);
  const general = providerName(t, 'GENERAL');
  const linked = (linkedProviders ?? []).filter(Boolean);
  if (linked.length === 0) return general;
  const names = linked.map((c) => providerName(t, c)).join(', ');
  return `${general} (${t('users.fields.loginTypeLinked', { names, defaultValue: `${names} 연동` })})`;
};

export const memberTypeLabel = (t: TFn, value: string | null | undefined): string =>
  value && MEMBER_TYPES.includes(value as MemberType)
    ? t(`users.memberType.${value}`)
    : FALLBACK;

/**
 * 국가몰 코드 → 라벨. 국적(nationality)과 달리 지원 코드가 4종으로 닫혀 있으므로
 * 미지원/누락 값은 코드 원문이 아닌 '—' 로 폴백한다(memberStatusLabel 과 동일 규칙).
 */
export const userMallLabel = (t: TFn, value: string | null | undefined): string =>
  asUserMall(value) ? t(`users.mall.${value}`) : FALLBACK;

/**
 * 회원정보 주소 표시 — "(우편번호) 주소 상세주소". 있는 것만 이어 붙인다: 상세주소가 없는 회원(약 2,400명)도
 * 끝에 공백이나 "****" 가 남지 않는다. 셋 다 비면 '—'. 상세주소 마스킹("****")은 BE 가 unmask 권한에 따라 정한다.
 */
export const formatMemberAddress = (
  postalCode: string | null | undefined,
  address: string | null | undefined,
  detail: string | null | undefined,
): string => {
  const clean = (v: string | null | undefined) => (typeof v === 'string' ? v.trim() : '');
  const parts = [
    clean(postalCode) ? `(${clean(postalCode)})` : '',
    clean(address),
    clean(detail),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : FALLBACK;
};

/** 국적 코드는 enum 집합이 열려 있어 raw 코드 폴백을 허용한다(미지원 코드도 코드 그대로). */
export const nationalityLabel = (t: TFn, value: string | null | undefined): string =>
  value ? t(`users.nationality.${value}`, value) : FALLBACK;
